import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";
import { TEST_IDS } from "../../constants/testIds";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "../ui/dropdown-menu";
import { toast } from "sonner";
import { BellOff, Bell } from "lucide-react";

const OPTIONS = [
  { label: "30 minutes", minutes: 30 },
  { label: "1 hour", minutes: 60 },
  { label: "2 hours", minutes: 120 },
  { label: "Today", minutes: 60 * 8 },
];

export default function DndToggle() {
  const { t } = useTranslation();
  const { user, fetchMe } = useAuthStore();
  const [dndUntil, setDndUntil] = useState(user?.dnd_until || null);

  useEffect(() => {
    setDndUntil(user?.dnd_until || null);
  }, [user?.dnd_until]);

  const now = Date.now();
  const active =
    dndUntil && new Date(dndUntil).getTime() > now ? dndUntil : null;

  const set = async (minutes) => {
    try {
      const { data } = await api.patch("/users/me/dnd", { minutes });
      setDndUntil(data.dnd_until);
      await fetchMe();
      if (minutes === 0) {
        toast.success(t("dnd.cleared"));
      } else {
        toast.success(t("dnd.enabled"));
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid={TEST_IDS.dndToggle}
          className={[
            "rounded-md border px-2 py-1.5 text-xs font-medium transition-colors",
            active
              ? "border-amber-300 bg-amber-50 text-amber-900"
              : "border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900",
          ].join(" ")}
        >
          {active ? (
            <BellOff className="inline h-3.5 w-3.5" />
          ) : (
            <Bell className="inline h-3.5 w-3.5" />
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs uppercase tracking-wider text-zinc-500">
          {t("dnd.title")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {OPTIONS.map((o) => (
          <DropdownMenuItem
            key={o.minutes}
            onSelect={() => set(o.minutes)}
            data-testid={TEST_IDS.dndOption(o.minutes)}
          >
            {o.label}
          </DropdownMenuItem>
        ))}
        {active && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => set(0)}
              data-testid={TEST_IDS.dndClear}
              className="text-red-600 focus:text-red-700"
            >
              {t("dnd.clear")}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
